window.CartRecommendations = (() => {
    let __swiperLoadPromise;

    const loadSwiperOnce = () => {
        if (window.Swiper) {
            return Promise.resolve();
        }

        if (__swiperLoadPromise) {
            return __swiperLoadPromise;
        }

        const load = (type, url) =>
            new Promise((resolve, reject) => {
                const selector =
                    type === 'css'
                        ? `link[href="${url}"]`
                        : `script[src="${url}"]`;

                if (document.querySelector(selector)) {
                    resolve();
                    return;
                }

                let el;

                if (type === 'css') {
                    el = document.createElement('link');
                    el.rel = 'stylesheet';
                    el.href = url;
                } else {
                    el = document.createElement('script');
                    el.src = url;
                }

                el.onload = resolve;
                el.onerror = reject;
                document.head.appendChild(el);
            });

        __swiperLoadPromise = Promise.all([
            load('css', 'https://unpkg.com/swiper/swiper-bundle.min.css'),
            load('js', 'https://unpkg.com/swiper/swiper-bundle.min.js')
        ]).then(() => {});

        return __swiperLoadPromise;
    };

    const enableDesktopSwipe = (root) => {
        const grid = root.querySelector('.recos__grid');
        if (!grid || grid.closest('.swiper')) return;
        if (window.innerWidth < 1024) return;

        loadSwiperOnce().then(() => {
            if (!window.Swiper) return;

            const swiperEl = document.createElement('div');
            swiperEl.className = 'swiper recos__swiper';

            const wrapper = document.createElement('div');
            wrapper.className = 'swiper-wrapper';

            [...grid.children].forEach((node) => {
                node.classList.add('swiper-slide');
                wrapper.appendChild(node);
            });

            const navWrap = document.createElement('div');
            navWrap.className = 'recos__nav-wrap';

            const prevBtn = document.createElement('button');
            prevBtn.type = 'button';
            prevBtn.className = 'recos__nav recos__nav--prev';
            prevBtn.setAttribute('aria-label', 'Previous');
            prevBtn.innerHTML = `<svg class="theme-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <path stroke="currentColor" stroke-miterlimit="10" stroke-width="1.5" d="M22 12H2"></path>
        <path stroke="currentColor" stroke-linejoin="round" stroke-width="1.5" d="M10.235 3.765 2 12l8.235 8.235"></path>
    </svg>`;

            const nextBtn = document.createElement('button');
            nextBtn.type = 'button';
            nextBtn.className = 'recos__nav recos__nav--next';
            nextBtn.setAttribute('aria-label', 'Next');
            nextBtn.innerHTML = `<svg class="theme-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <path stroke="currentColor" stroke-miterlimit="10" stroke-width="1.5" d="M2 12h20"></path>
        <path stroke="currentColor" stroke-linejoin="round" stroke-width="1.5" d="M13.765 20.235 22 12l-8.235-8.235"></path>
    </svg>`;

            navWrap.appendChild(prevBtn);
            navWrap.appendChild(nextBtn);

            grid.parentNode.replaceChild(swiperEl, grid);

            swiperEl.appendChild(wrapper);
            swiperEl.appendChild(navWrap);

            const sw = new Swiper(swiperEl, {
                slidesPerView: 'auto',
                spaceBetween: 12,
                freeMode: true,
                mousewheel: { forceToAxis: true },
                grabCursor: true,
                simulateTouch: true,
                threshold: 6,
                preventClicks: false,
                preventClicksPropagation: false,
                navigation: {
                    prevEl: prevBtn,
                    nextEl: nextBtn
                }
            });

            let dragMoved = false;

            sw.on('touchStart', () => {
                dragMoved = false;
            });

            sw.on('touchMove', () => {
                if (!dragMoved && Math.abs(sw.touches.diff) > 6) {
                    dragMoved = true;
                }
            });

            sw.on('touchEnd', () => {
                if (!dragMoved) return;

                const startedAt = performance.now();

                const guard = (ev) => {
                    if (performance.now() - startedAt <= 200 && ev.target.closest('a,button')) {
                        ev.preventDefault();
                        ev.stopPropagation();
                    }
                };

                swiperEl.addEventListener('click', guard, { capture: true, once: true });

                setTimeout(() => {
                    swiperEl.removeEventListener('click', guard, true);
                }, 220);
            });
        });
    };

    /**
     * Shopify Markets rate for the currency the visitor is actually browsing in.
     * Prices coming back from /recommendations/products.json are already in that
     * currency, so any threshold expressed in the shop's own currency has to be
     * converted before it can be compared against them.
     */
    const currencyRate = () => {
        const rate = Number(
            (window.Shopify && Shopify.currency && Shopify.currency.rate) || 1
        );

        return Number.isFinite(rate) && rate > 0 ? rate : 1;
    };

    const fmtPrice = (cents) => {
        const n = typeof cents === 'number' ? cents : Number(cents) || 0;

        // The theme's own money format, so non-USD markets render correctly.
        // Deliberately moneyFormat and not defaultMoneyFormat: the latter is the
        // with-currency-code variant whenever "Show currency codes" is on, which
        // would print "$39 USD" on a recommendation tile.
        if (typeof formatMoney === 'function') {
            return formatMoney(
                n,
                (window.theme && theme.moneyFormat) || '${{amount}}'
            );
        }

        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency:
                (window.Shopify && Shopify.currency && Shopify.currency.active) ||
                'USD',
            currencyDisplay: 'narrowSymbol',
            maximumFractionDigits: 0
        }).format(n / 100);
    };

    const pickAvailableVariant = (p) => {
        const variants = Array.isArray(p?.variants) ? p.variants : [];

        if (!variants.length) return null;

        const vWithQty = variants.find((v) => {
            const qty = typeof v?.inventory_quantity === 'number' ? v.inventory_quantity : null;
            return v && v.available === true && qty != null && qty > 0;
        });

        if (vWithQty) return vWithQty;

        const vAvailable = variants.find((v) => v && v.available === true);
        if (vAvailable) return vAvailable;

        return variants[0] || null;
    };

    const getDroppingSoonProductIdsCached = () => {
        if (window.__droppingSoonIds instanceof Set) {
            return Promise.resolve(window.__droppingSoonIds);
        }

        if (window.__droppingSoonIdsPromise) {
            return window.__droppingSoonIdsPromise;
        }

        window.__droppingSoonIdsPromise = fetch(
            `/collections/coming-soon/products.json?limit=250`,
            { credentials: 'same-origin' }
        )
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                const ids = new Set(
                    (data?.products || [])
                        .map((p) => Number(p.id))
                        .filter(Boolean)
                );

                window.__droppingSoonIds = ids;
                return ids;
            })
            .catch(() => {
                window.__droppingSoonIds = new Set();
                return window.__droppingSoonIds;
            })
            .finally(() => {
                window.__droppingSoonIdsPromise = null;
            });

        return window.__droppingSoonIdsPromise;
    };

    const getAnchorProductIdFromDOM = (root) => {
        const cartRoot = root.closest('[data-cart-items]') || document;

        const items = [...cartRoot.querySelectorAll(
            'li[data-product-id][data-final-price]'
        )];

        if (!items.length) return null;

        let bestEl = null;
        let bestPrice = -1;

        items.forEach((el) => {
            const price = Number(el.getAttribute('data-final-price')) || 0;
            if (price > bestPrice) {
                bestPrice = price;
                bestEl = el;
            }
        });

        return bestEl ? bestEl.getAttribute('data-product-id') : null;
    };

    const attachAddToCart = (root) => {
        root.addEventListener('click', (e) => {
            const btn = e.target.closest('.recos__btn');
            if (!btn) return;

            e.preventDefault();
            e.stopPropagation();

            if (btn.dataset.loading === '1') return;
            btn.dataset.loading = '1';

            const variantId = btn.getAttribute('data-variant-id');
            if (!variantId) {
                btn.dataset.loading = '0';
                return;
            }

            btn.textContent = 'ADDING!';

            const config =
                typeof fetchConfigDefaults === 'function'
                    ? fetchConfigDefaults()
                    : {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Accept: 'application/javascript'
                        }
                    };

            if (!config.headers) config.headers = {};
            if (!config.headers.Accept) config.headers.Accept = 'application/javascript';

            const sections =
                root.getAttribute('data-cart-sections') ||
                'drawer-cart-items,drawer-cart-footer,cart-item-count,cart-live-region';

            config.body = JSON.stringify({
                items: [{ id: variantId, quantity: 1 }],
                sections,
                sections_url: window.location.pathname
            });

            const endpoint =
                window.theme && theme.routes && theme.routes.cart_add_url
                    ? theme.routes.cart_add_url
                    : '/cart/add';

            btn.disabled = true;

            fetch(endpoint, config)
                .then((res) => res.json())
                .then((data) => {
                    if (data && data.status === 422) {
                        btn.disabled = false;
                        btn.dataset.loading = '0';
                        return;
                    }

                    console.log(data)

                    if (data && data.sections) {
                        document.body.dispatchEvent(
                            new CustomEvent('switch:cart:afteradditem', {
                                bubbles: true,
                                detail: { response: data }
                            })
                        );
                    }

                    if (window.Alpine && Alpine.store && Alpine.store('cart_count')) {
                        fetch('/cart.js', { credentials: 'same-origin' })
                            .then((r) => r.json())
                            .then((cart) => {
                                const s = Alpine.store('cart_count');
                                if (typeof s.set === 'function') s.set(cart.item_count);
                                if ('count' in s) s.count = cart.item_count;
                            });
                    }

                    document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true }));

                    btn.dataset.loading = '0';
                })
                .catch(() => {
                    btn.disabled = false;
                    btn.dataset.loading = '0';
                });
        });
    };

    const render = (root, products) => {
        const grid = root.querySelector('.recos__grid');
        if (!grid) return;

        grid.innerHTML = '';

        products.forEach((p) => {
            const url = p.url || (p.handle ? `/products/${p.handle}` : '#');
            const img = p.featured_image || (Array.isArray(p.images) && p.images[0]) || '';

            const v = pickAvailableVariant(p);

            const priceCents =
                typeof p.price === 'number'
                    ? p.price
                    : v && v.price != null
                        ? v.price
                        : null;

            const price = priceCents != null ? fmtPrice(Number(priceCents)) : '';
            const variantId = v ? v.id : '';

            const a = document.createElement('a');
            a.className = 'recos__item';
            a.href = url;
            a.setAttribute('aria-label', p.title || '');
            
            if (products.length === 1) {
                a.style.maxWidth = '100%';
            }

            a.innerHTML =
                (img
                    ? `<img class="recos__img" loading="lazy" src="${img}" alt="${p.title || ''}">`
                    : '') +
                `<div class="recos__cont flex flex-col justify-center">` +
                    `<div class="recos__title capitalize">${p.title || ''}</div>` +
                    `<div class="recos__cont-action flex items-center">` +
                        (price ? `<div class="recos__price capitalize">${price}</div>` : '') +
                        `<button class="recos__btn text-center uppercase text-white" type="button" data-variant-id="${variantId}">Add to Cart</button>` +
                    `</div>` +
                `</div>`;

            grid.appendChild(a);
        });

        enableDesktopSwipe(root);
        attachAddToCart(root);
    };

    const fetchRecommendations = async (root) => {
        const productId = getAnchorProductIdFromDOM(root);
        if (!productId) {
            root.style.display = 'none';
            return;
        }

        const excludeId = Number(root.getAttribute('data-exclude-id') || 0);
        // data-max-price is expressed in the shop's own currency subunits
        // (5000 = $50.00); convert it into the currency the visitor sees.
        const maxPrice =
            Number(root.getAttribute('data-max-price') || 5000) * currencyRate();

        const res = await fetch(
            `/recommendations/products.json?product_id=${productId}&limit=20`,
            { credentials: 'same-origin' }
        ).then((r) => (r.ok ? r.json() : null));

        const droppingSoonIds = await getDroppingSoonProductIdsCached();

        const limit = Number(root.getAttribute('data-limit') || 3);

        const products = (res?.products || [])
        .filter((p) => {
            const price =
                typeof p.price === 'number'
                    ? p.price
                    : p.variants?.[0]?.price || 0;

            const pid = Number(p.id);

            return p.id !== excludeId && price > 0 && price < maxPrice && !droppingSoonIds.has(pid);
        })
        .slice(0, limit);

        if (!products.length) {
            root.style.display = 'none';
            return;
        }

        root.style.display = '';
        render(root, products);
    };

    const init = (root) => {
        if (!root || root.dataset.recosInit === '1') return;
        root.dataset.recosInit = '1';
        fetchRecommendations(root);
    };

    return { init };
})();
